import { Component, Inject, OnDestroy } from '@angular/core';
import { DocumentNode } from '@completion/models';
import { Store } from '@ngrx/store';
import { selectEditMode, getCurrentDocument } from '@completion/selectors';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { DocumentUpdateQueueService } from '../../../../core/services/document-update-queue.service';
import { persistNodeUpdateForNode } from '../node-persistence.util';

@Component({
  selector: 'app-heading',
  templateUrl: './heading.component.html',
  styleUrls: ['./heading.component.scss'],
  standalone: false,
})
export class HeadingComponent implements OnDestroy {
  documentNode: DocumentNode;
  elementId: string;

  editMode = false;
  editing = false;
  headingControl = new FormControl('');
  private destroy$ = new Subject<void>();
  originalValue = '';
  displayContent = 'New Heading';

  constructor(
    @Inject('documentNode') documentNode: DocumentNode,
    private store: Store,
    private updateQueue: DocumentUpdateQueueService,
  ) {
    this.documentNode = documentNode;
    if (documentNode && documentNode.id != null) {
      this.elementId = `section-${documentNode.id}`;
    } else {
      const seed =
        documentNode && (documentNode.content || documentNode.name) ? String(documentNode.content || documentNode.name) : `heading-unknown`;
      this.elementId = this.generateElementId(seed);
    }
    const initial = this.documentNode.content || this.documentNode.name || 'New Heading';
    this.headingControl.setValue(initial);
    this.originalValue = initial;
    this.displayContent = initial;
    this.store
      .select(selectEditMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((m) => (this.editMode = !!m));
  }

  startEdit(): void {
    if (!this.editMode) {
      return;
    }
    this.editing = true;
    this.originalValue = this.displayContent;
    this.headingControl.setValue(this.originalValue);
  }

  cancelEdit(): void {
    this.editing = false;
    this.headingControl.setValue(this.originalValue);
  }

  saveEdit(): void {
    const valRaw = (this.headingControl.value || '').trim();
    const val = valRaw.length ? valRaw : 'New Heading';
    this.editing = false;
    this.displayContent = val;
    this.elementId = this.documentNode.id != null ? `section-${this.documentNode.id}` : this.generateElementId(val);
    // Persist immutably without mutating injected node
    persistNodeUpdateForNode(this.store, this.updateQueue, this.documentNode, (n) => {
      n.content = val;
      n.name = val;
    });
  }

  onInputKey(ev: KeyboardEvent): void {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      this.saveEdit();
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      this.cancelEdit();
    }
  }

  onBlur(): void {
    if (this.editing) {
      this.saveEdit();
    }
  }

  removeHeading(): void {
    if (!this.editMode) {
      return;
    }
    // Remove this node from the document tree immutably
    this.store
      .select(getCurrentDocument)
      .pipe(take(1))
      .subscribe((doc) => {
        if (!doc) {
          return;
        }
        const cloned = JSON.parse(JSON.stringify(doc));
        const removeNode = (nodes: any[]): any[] => {
          let changed = false;
          const filtered = nodes
            .filter((n) => {
              if (n && n.id === (this.documentNode as any).id) {
                changed = true;
                return false;
              }
              return true;
            })
            .map((n) => {
              if (n && n.children && n.children.length) {
                const newChildren = removeNode(n.children);
                if (newChildren !== n.children) {
                  return { ...n, children: newChildren };
                }
              }
              return n;
            });
          return changed || filtered.some((n, i) => n !== nodes[i]) ? filtered : nodes;
        };
        cloned.nodes = removeNode(cloned.nodes || []);
        this.updateQueue.queue(cloned);
      });
  }

  private generateElementId(content: string): string {
    if (!content) {
      return 'heading-unknown';
    }
    return String(content)
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
